import React, { useState, ReactNode, ReactElement } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";

interface ResponsiveLayoutProps {
  header?: ReactNode;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  mobileNavButtons?: ReactNode;
  showBothSides?: boolean;
  externalHeaderHeight?: number; // Hauteur supplémentaire à soustraire (en pixels)
  // Props contrôlées optionnelles
  activeTab?: "left" | "right";
  onTabChange?: (tab: "left" | "right") => void;
  /** Desktop : panneau gauche partagé (défaut), réduit en barre, ou masqué. */
  leftPanelMode?: "split" | "collapsed" | "hidden";
  /** Barre étroite affichée à la place du panneau gauche en mode réduit. */
  /** Superposé au panneau droit (ex. bouton pour rouvrir le panneau gauche). */
  rightOverlay?: ReactNode;
}

/**
 * ResponsiveLayout - Composant de layout responsive centralisé
 *
 * Gère UNIQUEMENT le layout responsive (mobile/tablette/desktop)
 * - Détection responsive (source de vérité unique)
 * - Positionnement header/boutons/contenu
 * - Calcul des dimensions (source de vérité unique)
 * - Gestion des z-index
 *
 * Les composants enfants reçoivent les dimensions et n'ont pas besoin
 * de gérer le responsive eux-mêmes.
 */
export function ResponsiveLayout({
  header,
  leftContent,
  rightContent,
  mobileNavButtons,
  showBothSides = true,
  externalHeaderHeight = 0,
  activeTab: controlledActiveTab,
  onTabChange: controlledOnTabChange,
  leftPanelMode = "split",
  rightOverlay,
}: ResponsiveLayoutProps) {
  // SOURCE DE VÉRITÉ UNIQUE pour le responsive
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1023px)");

  // État interne si non contrôlé
  const [internalActiveTab, setInternalActiveTab] = useState<"left" | "right">(
    "left",
  );

  // Utiliser l'état contrôlé si fourni, sinon l'état interne
  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;
  const setActiveTab =
    isControlled && controlledOnTabChange
      ? controlledOnTabChange
      : setInternalActiveTab;

  // Log pour déboguer
  console.log("ResponsiveLayout - État:", {
    controlledActiveTab,
    isControlled,
    activeTab,
    isMobile,
    isTablet,
  });

  // SOURCE DE VÉRITÉ UNIQUE pour les dimensions
  const DIMENSIONS = {
    header: 64,
    mobileNav: isMobile || isTablet ? 48 : 0,
    externalHeader: externalHeaderHeight,
    get availableHeight() {
      return `calc(100dvh - ${this.header + this.mobileNav}px)`;
    },
  };

  return (
    <div className="flex flex-col flex-1 h-screen-safe overflow-hidden bg-background">
      {/* Header - toujours fixe en haut */}
      {header && (
        <div
          className="fixed top-0 left-0 right-0 z-50 bg-background"
          style={{ height: DIMENSIONS.header }}
        >
          {header}
        </div>
      )}

      {/* Contenu principal */}
      <div
        className="flex-1 flex"
        style={{
          height: DIMENSIONS.availableHeight,
          paddingTop: header ? `${DIMENSIONS.header}px` : 0,
        }}
      >
        {/* Desktop : 50/50 côte à côte */}
        {!isMobile && !isTablet && showBothSides ? (
          <>
            {/* Toujours monté (classe hidden) pour garder l'état du chat.
                Réduit = moitié de la largeur du mode 50/50. */}
            <div
              className={`h-full overflow-hidden shrink-0 ${
                leftPanelMode === "split"
                  ? "w-1/2"
                  : leftPanelMode === "collapsed"
                    ? "w-1/4 border-r border-border"
                    : "hidden"
              }`}
              data-testid="responsive-left-panel"
            >
              {leftContent}
            </div>
            <div
              className={`relative h-full overflow-hidden ${leftPanelMode === "split" ? "w-1/2" : "flex-1"}`}
            >
              {rightContent}
              {leftPanelMode !== "split" && rightOverlay}
            </div>
          </>
        ) : (
          /* Mobile/Tablette : un seul côté visible via CSS pour garder l'état monté */
          <>
            <div
              className={`w-full h-full ${activeTab === "left" ? "block" : "hidden"}`}
              style={{
                paddingBottom:
                  (isMobile || isTablet) && mobileNavButtons ? "48px" : "0",
              }}
            >
              {leftContent}
            </div>
            <div
              className={`w-full h-full ${activeTab === "right" ? "block" : "hidden"}`}
              style={{
                paddingBottom:
                  (isMobile || isTablet) && mobileNavButtons ? "48px" : "0",
              }}
            >
              {rightContent}
            </div>
          </>
        )}
      </div>

      {/* Boutons navigation mobile - toujours fixes en bas */}
      {(isMobile || isTablet) && mobileNavButtons && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[60] bg-background"
          style={{ height: DIMENSIONS.mobileNav }}
        >
          {React.cloneElement(mobileNavButtons as ReactElement, {
            activeTab: activeTab === "left" ? "chat" : "editor", // Convertir pour MobileNavigationTabs
            onTabChange: (tab) =>
              setActiveTab(tab === "chat" ? "left" : "right"), // Convertir le retour
          })}
        </div>
      )}
    </div>
  );
}
